/**
 * Tenant config resolved by hostname (unified admin portal).
 *
 * ATT-3383: tenant resolution is registry-only. Every host resolves via the
 * consolidated backend tenant registry — there is no bundled static tenant map
 * and no allowlist gate. `getTenantConfig` returns the host-keyed routing cache
 * entry once discovery has resolved, or a first-paint bootstrap pointed at
 * `NEXT_PUBLIC_API_BASE` (with `X-Tenant-Domain` = the host) while discovery is
 * in flight. `NEXT_PUBLIC_API_BASE` is required; its absence is a fail-loud
 * throw (never a silent fall-through to localhost).
 */

import * as Sentry from "@sentry/nextjs";
import { getRoutingForHost, __resetForTests as resetRoutingCache } from "../lib/tenantDiscovery/routingCache";
import { getOverrideBranding, __resetForTests as resetOverrideBranding } from "../lib/tenantOverride/store";

export interface TenantConfig {
  apiUrl: string;
  cookieDomain: string | false;
  districtName: string;
  featureFlags: string[];
  hideLanguages: string[];
  logoUrl: string | false;
  publicBase: string;
  /**
   * ATT-2871 overlay flag. When true, the admin app fetches client-readable
   * settings from the backend `tenantSettings` query (via `useTenantSettings`)
   * and overlays the values for keys it consumes. Registry-resolved configs
   * leave this `undefined` — branding is hydrated by tenant discovery directly.
   */
  useBackendSettings?: boolean;
}

/**
 * Normalize host for lookup. Mirrors the consolidated-backend resolver (ATT-2866):
 * lowercase, strip leading scheme (http/https), strip port, strip a single
 * trailing dot (FQDN form `host.` that some browsers/clients keep verbatim).
 */
export function normalizeHost(host: string): string {
  let s = host.trim().toLowerCase();
  if (s.startsWith("http://")) s = s.slice("http://".length);
  else if (s.startsWith("https://")) s = s.slice("https://".length);
  if (s.includes(":")) s = s.slice(0, s.indexOf(":"));
  if (s.endsWith(".")) s = s.slice(0, -1);
  return s;
}

/**
 * Test-only: reset the host-keyed routing cache so cases don't leak resolved
 * routing into each other.
 */
export function __resetForTests(): void {
  resetRoutingCache();
  resetOverrideBranding();
}

/** ATT-3383: build a TenantConfig from registry-resolved routing (cache hit). */
function buildDynamicConfig(
  normalizedHost: string,
  cached: { apiUrl: string; cookieDomain: string | null; publicBase: string | null },
): TenantConfig {
  return {
    apiUrl: cached.apiUrl,
    cookieDomain: cached.cookieDomain ?? false,
    publicBase: cached.publicBase ?? `https://${normalizedHost}`,
    districtName: "",
    featureFlags: [],
    hideLanguages: [],
    logoUrl: false,
    useBackendSettings: undefined,
  };
}

/**
 * ATT-3383: first-paint bootstrap config for a host whose registry routing has
 * not resolved yet. `apiUrl` is the single `NEXT_PUBLIC_API_BASE` endpoint so
 * the first GraphQL request (the resolve itself) targets a valid, public,
 * cross-domain-safe endpoint with `X-Tenant-Domain`. `NEXT_PUBLIC_API_BASE`
 * unset is a fail-loud throw (must NOT fall through to localhost). Routing
 * fields are NEVER derived from `COOKIE_DOMAIN` / `NEXT_PUBLIC_BASE` env vars —
 * `cookieDomain` is bootstrap-safe (`false`) and the server resolves the real
 * cookie domain from the registry with a fail-closed fallback chain. Branding
 * fields start empty and are hydrated post-boot by
 * `lib/tenantDiscovery/useTenantDiscovery`.
 */
function bootstrapDynamicConfig(normalizedHost: string): TenantConfig {
  const apiBase = process.env.NEXT_PUBLIC_API_BASE;
  if (!apiBase) {
    const message =
      "NEXT_PUBLIC_API_BASE is not set. Tenant resolution is registry-only and " +
      "the bootstrap resolve has no endpoint. Refusing to fall through to localhost — " +
      "set NEXT_PUBLIC_API_BASE to the consolidated backend URL (e.g. https://api.attendly.com).";
    // eslint-disable-next-line no-console
    console.warn(`[tenant-config] ${message}`);
    Sentry.addBreadcrumb({
      category: "tenant-config",
      level: "error",
      message,
      data: { host: normalizedHost },
    });
    throw new Error(message);
  }
  return {
    apiUrl: apiBase,
    cookieDomain: false,
    publicBase: `https://${normalizedHost}`,
    districtName: "",
    featureFlags: [],
    hideLanguages: [],
    logoUrl: false,
    useBackendSettings: undefined,
  };
}

/**
 * Returns tenant config for the given host (ATT-3383 — registry-only).
 *
 * 1. Registry routing cache hit → the registry-resolved config.
 * 2. Cache miss → first-paint bootstrap pointed at `NEXT_PUBLIC_API_BASE`
 *    (with `X-Tenant-Domain` = the host) while discovery resolves.
 *
 * There is no static-map branch and no allowlist branch. A genuinely unknown
 * host is rejected by the backend's `X-Tenant-Domain` + `Origin`/`Referer`
 * cross-check (ATT-2866); the client surfaces a loading/retry state until
 * discovery resolves (see `lib/tenantDiscovery`).
 */
export function getTenantConfig(host: string): TenantConfig {
  const normalized = normalizeHost(host);
  const cached = getRoutingForHost(normalized);
  if (cached) return buildDynamicConfig(normalized, cached);
  return bootstrapDynamicConfig(normalized);
}

/* ------------------------------------------------------------------------- *
 * ATT-3226: staging-only QA tenant override (branding-only, gate-flagged).
 *
 * A QA on staging simulates any tenant's BRANDING by visiting
 * `…/?__tenant=<host>` (persisted in the `attendly_tenant_override` cookie),
 * while auth + backend routing stay pinned to the real staging tenant
 * (Option A). The whole mechanism is INERT unless
 * `NEXT_PUBLIC_ALLOW_TENANT_OVERRIDE === "true"` (strict equality) — production
 * projects never set that var, so a forged cookie/query is ignored there.
 * Full spec: openspec/changes/att-3226.
 *
 * ATT-3384 re-wire: the override target's BRANDING is fetched from the backend
 * `tenantBrandingByHost(host, portal)` query (which resolves the tenant by the
 * `host` ARG, not the request header) into the override-branding store
 * (`lib/tenantOverride/`). An override target is "resolvable" once that fetch
 * returns non-null branding; until then (or for an unknown host) the real
 * staging config is used with no partial merge. Auth/data (`apiUrl`,
 * `cookieDomain`) ALWAYS stay the real staging values — only branding +
 * (host-derived) `publicBase` switch to the target.
 * ------------------------------------------------------------------------- */

export const TENANT_OVERRIDE_COOKIE = "attendly_tenant_override";
const TENANT_OVERRIDE_QUERY = "__tenant";
const TENANT_OVERRIDE_CLEAR = "clear";

/** Strict gate — ONLY the exact string "true" enables the override (PM-1). */
function isTenantOverrideEnabled(): boolean {
  return process.env.NEXT_PUBLIC_ALLOW_TENANT_OVERRIDE === "true";
}

/** Read a single cookie value from a raw Cookie header / `document.cookie`. */
function readCookieValue(cookieHeader: string | null | undefined, name: string): string | null {
  if (!cookieHeader) return null;
  const m = cookieHeader.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Read the raw `__tenant` value from the current client URL.
 *
 * Looks at the top-level query first, then inside a nested `callbackUrl` — when
 * a `?__tenant=` deep-link hits a protected route, NextAuth redirects to
 * `/sign-in?callbackUrl=<original-url>&error=SessionRequired`, relocating the
 * `__tenant` param INSIDE `callbackUrl`. Without this, the override could not be
 * set/changed from a deep-link that bounces through the sign-in page, and a
 * stale cookie would stay stuck (ATT-3226 QA bug). Returns the raw value (or
 * null); gate + normalization are the caller's job.
 */
function readOverrideParamFromClientUrl(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const direct = params.get(TENANT_OVERRIDE_QUERY);
  if (direct !== null) return direct;
  const callbackUrl = params.get("callbackUrl");
  if (callbackUrl) {
    try {
      const nested = new URL(callbackUrl, window.location.origin).searchParams.get(
        TENANT_OVERRIDE_QUERY
      );
      if (nested !== null) return nested;
    } catch {
      /* malformed callbackUrl — ignore */
    }
  }
  return null;
}

/**
 * Normalize a raw override value (from query or cookie) to a target host, or
 * null. `clear` and empty/whitespace → null (no override). Anything else is
 * passed through `normalizeHost`. Does NOT check the gate — callers do.
 */
function normalizeOverrideValue(raw: string | null): string | null {
  if (raw === null) return null;
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed.toLowerCase() === TENANT_OVERRIDE_CLEAR) return null;
  return normalizeHost(trimmed);
}

/**
 * The active override target host, or null when no override is in effect.
 *
 * - **Gate off** → always null (production inertness).
 * - **Client** → reads the `?__tenant=` query param FIRST (so the boot-time
 *   tenant-settings hydration on a deep-link already observes the target — no
 *   first-paint flash, design.md §D6), then the `attendly_tenant_override`
 *   cookie.
 * - **Server** → reads the override carried request-scoped in the request-host
 *   `AsyncLocalStorage` (set by `withRequestHost`); NEVER a module global, so
 *   concurrent SSR requests cannot cross-contaminate (PM-2).
 *
 * SECURITY: auth / `serverTenantResolve` / NextAuth MUST NOT branch on this —
 * they resolve the real host. The Option-A merge in `getCurrentTenantConfig`
 * pins `apiUrl`/`cookieDomain` to staging regardless.
 */
export function getTenantOverrideHost(): string | null {
  if (!isTenantOverrideEnabled()) return null;
  if (typeof window !== "undefined") {
    const qp = readOverrideParamFromClientUrl();
    if (qp !== null) return normalizeOverrideValue(qp);
    return normalizeOverrideValue(readCookieValue(document.cookie, TENANT_OVERRIDE_COOKIE));
  }
  const store = getRequestHostStorage()?.getStore();
  const override = store && typeof store === "object" ? (store.override ?? null) : null;
  return normalizeOverrideValue(override);
}

/**
 * Is `normalizedHost` a KNOWN tenant whose branding we can simulate? ATT-3384:
 * a target is resolvable once `tenantBrandingByHost` has returned non-null
 * branding for it (fetched by `useTenantOverrideBranding` into the override-
 * branding store). An override target that has not been fetched yet, or that the
 * backend resolved to null (unknown/inactive host), is NOT resolvable → the
 * Option-A merge falls back to the real staging config with NO partial merge.
 */
function isResolvableTenantHost(normalizedHost: string): boolean {
  if (!normalizedHost) return false;
  return getOverrideBranding(normalizedHost) !== null;
}

/**
 * Display name of the active override target once its branding has been fetched
 * (ATT-3384), else null. Drives the staging-only "Simulating tenant: …" banner.
 * Gate-aware (via `getTenantOverrideHost`). Returns null until the
 * `tenantBrandingByHost` fetch resolves (or when it returns no display name), so
 * the banner stays hidden rather than showing an empty label.
 */
export function getActiveOverrideTenantName(): string | null {
  const overrideHost = getTenantOverrideHost();
  if (!overrideHost) return null;
  const normalized = normalizeHost(overrideHost);
  if (!isResolvableTenantHost(normalized)) return null;
  return getOverrideBranding(normalized)?.displayName ?? null;
}

/**
 * Client cookie-writer for the override. Call from a `useEffect` in `_app`.
 * Gate-guarded. `?__tenant=clear` → delete the cookie; `?__tenant=<host>`
 * (non-empty) → persist it as a SESSION cookie (`Path=/`, `SameSite=Lax`, no
 * expiry). No-op when the gate is off or no `__tenant` param is present.
 */
export function syncTenantOverrideCookie(): void {
  if (typeof window === "undefined") return;
  if (!isTenantOverrideEnabled()) return;
  const qp = readOverrideParamFromClientUrl();
  if (qp === null) return;
  const trimmed = qp.trim();
  if (trimmed.toLowerCase() === TENANT_OVERRIDE_CLEAR) {
    document.cookie = `${TENANT_OVERRIDE_COOKIE}=; Path=/; SameSite=Lax; Max-Age=0`;
    return;
  }
  if (trimmed === "") return;
  document.cookie = `${TENANT_OVERRIDE_COOKIE}=${encodeURIComponent(trimmed)}; Path=/; SameSite=Lax`;
}

/**
 * Request-scoped host on server. Lazy-loaded because async_hooks is Node-only
 * and this file is also bundled for the client.
 * Call getRequestHostStorage().enterWith(host) at the start of getServerSideProps.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _requestHostStorage: any = null;

export function getRequestHostStorage() {
  if (!_requestHostStorage && typeof window === "undefined") {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { AsyncLocalStorage } = require("async_hooks");
    _requestHostStorage = new AsyncLocalStorage();
  }
  return _requestHostStorage;
}

/**
 * ATT-3226: the REAL request/window host, IGNORING any override. Auth and the
 * Option-A merge use this to pin backend/auth fields to the genuine staging
 * tenant. `getCurrentHost()` wraps this and substitutes the override target.
 *
 * Server reads the host from the request-scoped store written by
 * `withRequestHost` — defensive against both the new `{ host, override }`
 * object shape and a bare host string (older callers / tests).
 */
function getRealCurrentHost(): string {
  if (typeof window !== "undefined") {
    return window.location.hostname;
  }
  const storage = getRequestHostStorage();
  const store = storage?.getStore();
  const fromRequest =
    store && typeof store === "object" ? store.host : (store as string | undefined);
  if (fromRequest) return fromRequest;
  if (process.env.VERCEL_URL) {
    return process.env.VERCEL_URL.replace(/^https?:\/\//, "").split("/")[0];
  }
  if (process.env.NEXT_PUBLIC_BASE) {
    try {
      return new URL(process.env.NEXT_PUBLIC_BASE).hostname;
    } catch {
      return "";
    }
  }
  return "";
}

/**
 * Current host: browser = window.location.hostname; server = from request (if set via
 * getRequestHostStorage().enterWith in getServerSideProps) or env (VERCEL_URL / NEXT_PUBLIC_BASE).
 *
 * ATT-3226: when a staging override is active, returns the OVERRIDE target host.
 * Safe because no consumer outside this module branches on `getCurrentHost()`
 * for auth/backend routing — `X-Tenant-Domain` derives from
 * `window.location.hostname` directly (lib/util.ts), and the Option-A merge in
 * `getCurrentTenantConfig` re-pins auth/backend fields off the REAL host.
 */
export function getCurrentHost(): string {
  const override = getTenantOverrideHost();
  if (override) return override;
  return getRealCurrentHost();
}

/**
 * Convenience: tenant config for the current request/host.
 *
 * ATT-3226 Option-A merge (re-wired in ATT-3384): when a staging override target
 * has resolved branding, return that target's BRANDING (display name, logo,
 * feature flags, hidden languages) with the auth/backend fields (`apiUrl`,
 * `cookieDomain`) kept pinned to the REAL staging tenant. `publicBase` is DERIVED
 * from the override host (the backend branding query intentionally does not return
 * it) so portal helpers like `isNorris()` / `publicBase().includes(...)` flip to
 * the simulated tenant. Unknown/unfetched target → real staging config, NO
 * partial merge. Gate off → unchanged behaviour.
 */
export function getCurrentTenantConfig(): TenantConfig {
  const realHost = getRealCurrentHost() || "localhost";
  const overrideHost = getTenantOverrideHost();
  if (!overrideHost) return getTenantConfig(realHost);

  const normalizedOverride = normalizeHost(overrideHost);
  if (!isResolvableTenantHost(normalizedOverride)) return getTenantConfig(realHost);

  const staging = getTenantConfig(realHost);
  const branding = getOverrideBranding(normalizedOverride);
  return {
    ...staging, // apiUrl + cookieDomain STAY the real staging values (auth/data pinned)
    publicBase: `https://${normalizedOverride}`,
    districtName: branding?.displayName ?? "",
    logoUrl: branding?.logoUrl ?? false,
    featureFlags: branding?.featureFlags ?? [],
    hideLanguages: branding?.hideLanguages ?? [],
  };
}

/**
 * Extract hostname from an incoming Next.js API request.
 * Prefers x-forwarded-host (set by Vercel/reverse-proxy), falls back to Host header.
 */
function hostFromRequest(req: { headers: Record<string, string | string[] | undefined> }): string {
  const raw = req.headers["x-forwarded-host"] ?? req.headers.host ?? "";
  const value = Array.isArray(raw) ? raw[0] : raw;
  return normalizeHost(value ?? "");
}

/**
 * Wrap a Next.js API handler so that `getCurrentHost()` / `getCurrentTenantConfig()`
 * return the correct tenant for the duration of the request.
 *
 * Usage:
 *   export default withRequestHost((req, res) => { ... });
 */
import type { NextApiRequest, NextApiResponse } from "next";

export function withRequestHost(
  handler: (req: NextApiRequest, res: NextApiResponse) => unknown,
) {
  return (req: NextApiRequest, res: NextApiResponse) => {
    const host = hostFromRequest(req);
    // ATT-3226: carry the override cookie request-scoped (NEVER a module
    // global) so SSR resolves the same target as the client and concurrent
    // requests with different overrides cannot cross-contaminate (PM-2).
    const rawCookie = req.headers.cookie;
    const cookieHeader = Array.isArray(rawCookie) ? rawCookie[0] : rawCookie;
    const override = readCookieValue(cookieHeader ?? null, TENANT_OVERRIDE_COOKIE);
    const storage = getRequestHostStorage();
    if (storage) {
      return storage.run({ host, override }, () => handler(req, res));
    }
    return handler(req, res);
  };
}
